import {
  ResponseResult,
  ResponseStatus,
  type QuestionResponseDetails,
  type SubQuestionResponseEntry,
  type UpdateAuditQuestionResponseCommandInput,
} from '@amzn/gbsa-helios-typescript-client';
import {
  buildPreservedResponseData,
  projectSubResponsesForPreservation,
} from './preserveResponseFields';

/**
 * Arguments for {@link buildAnswerUpdate}. Passed as a single object
 * so future fields (attachments, NA-reason id, sub-question answer
 * override, `expectedLastUpdatedAt`) slot in without shifting args.
 */
export interface BuildAnswerUpdateArgs {
  readonly businessUnitId: string;
  readonly auditId: string;
  readonly questionId: string;
  /** Current user alias — sent as `respondedBy` AND `lastUpdatedBy`. */
  readonly alias: string;
  /** Pass / Fail / N/A from the auditor's tap. */
  readonly result: ResponseResult;
  /**
   * The question's current cached response. Comment, naReason, and
   * media ids are preserved onto the outgoing payload — the mutation
   * is a full-PATCH, so any field NOT re-asserted here is cleared on
   * the server. Undefined when the question has never been answered.
   *
   * Typed as `T | undefined` (not just `T`) so a caller explicitly
   * passing `undefined` still type-checks under
   * `exactOptionalPropertyTypes: true`.
   */
  readonly previous?: QuestionResponseDetails | undefined;
  /**
   * Sub-question responses on the current question. Preserved
   * verbatim (result + comment + naReason + mediaIds + taskIds)
   * onto the outgoing `subResponses` via
   * {@link projectSubResponsesForPreservation}. Answering a parent
   * that has sub-questions isn't a supported UI path today (CR-V4
   * hides the parent buttons in that case) — this preservation is
   * defense-in-depth so a stray parent-answer save can't wipe
   * server-side sub-answers or their comments / naReason / media.
   */
  readonly subQuestionResponses?: readonly SubQuestionResponseEntry[] | undefined;
}

/**
 * Builds the mutation input for a Pass / Fail / N/A answer.
 *
 * Pure — no react, no hooks, no side effects. Consumed by
 * `useQuestionResponseDraft` in `features/audit/hooks/`.
 *
 * Design constraints baked in:
 *   - `responseStatus` maps by result: `NA` → `ResponseStatus.NA`,
 *     `PASS`/`FAIL` → `ResponseStatus.COMPLETED`. Getting this wrong
 *     would report NA answers under the "completed" workflow bucket.
 *   - Parent `response.{comment,naReason,mediaIds,taskIds}` are
 *     preserved via the shared {@link buildPreservedResponseData}
 *     helper. Sub-responses preserve the same set of fields
 *     per-child via {@link projectSubResponsesForPreservation} —
 *     both call sites in `logic/` funnel through the same
 *     preservation code so a fix or field addition in one path
 *     lands in the other for free.
 *   - `expectedLastUpdatedAt` is NOT set. The audit-summary payload's
 *     `QuestionResponseDetails` doesn't carry a record-level
 *     concurrency token, so passing anything would guess. When
 *     multi-assignee conflict detection lands, either the summary
 *     extends to expose the token or a per-response fetch supplies it.
 */
export function buildAnswerUpdate(
  args: BuildAnswerUpdateArgs,
): UpdateAuditQuestionResponseCommandInput {
  const responseStatus =
    args.result === ResponseResult.NA ? ResponseStatus.NA : ResponseStatus.COMPLETED;

  const input: UpdateAuditQuestionResponseCommandInput = {
    businessUnitId: args.businessUnitId,
    auditId: args.auditId,
    questionId: args.questionId,
    responseStatus,
    responseResult: args.result,
    respondedBy: args.alias,
    lastUpdatedBy: args.alias,
  };

  // Clear naReason on the wire when the new result isn't NA —
  // otherwise a previously-picked reason survives an answer change
  // and leaks a stale label into the server state. Undefined =
  // preserve (the NA case); `null` = clear (drops naReason and
  // naReasonId from the wire under the widened
  // buildPreservedResponseData contract).
  const naReasonOverride = args.result === ResponseResult.NA ? undefined : null;
  const preservedResponse = buildPreservedResponseData(args.previous, {
    naReasonOverride,
  });
  if (preservedResponse) {
    input.response = preservedResponse;
  }

  if (args.subQuestionResponses && args.subQuestionResponses.length > 0) {
    // Full preservation — every existing sub-response keeps its
    // result / comment / naReason / mediaIds / taskIds on the wire.
    // Before CR-V6 this path emitted `{ subQuestionId, result }`
    // only, silently wiping server-side comments on sibling subs.
    input.subResponses = projectSubResponsesForPreservation(args.subQuestionResponses);
  }

  return input;
}
